import * as React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'lib/intl';

import Checkbox from 'components/Checkbox';
import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import EventTracking from 'lib/EventTracking';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';

type Props = {
  orgUuid: string | undefined | null;
  onConfirm: (messageOptions?: Record<string, any>) => void;
  onRequestHide: () => void;
  totalRecipientCount: number;
  scheduled?: boolean;
  eventProperties?: any;
};

type State = {
  callAllDevices: boolean;
};

class SendUrgentMessageConfirmDialog extends React.Component<Props> {
  state: State = {
    callAllDevices: false,
  };

  // no need to specify this is an urgent message send in the event name
  // because is_urgent is already passed through |eventProperties|
  componentDidMount() {
    EventTracking.trackKey('announcements.composer.confirm_send.view', {
      organization_uuid: this.props.orgUuid,
      ...this.props.eventProperties,
    });
  }

  render() {
    const hasCallAllDevicesFeature = CurrentUser.getUser()?.hasFeature('call_all_devices.feb-23');
    const { totalRecipientCount, onConfirm, onRequestHide, scheduled, orgUuid } = this.props;
    const titleText = scheduled ? (
      <FormattedMessage
        id="composer.urgentMessageConfirmDialog.title.scheduled"
        values={{ totalRecipientCount }}
      />
    ) : (
      <FormattedMessage
        id="composer.urgentMessageConfirmDialog.title.unscheduled"
        values={{ totalRecipientCount }}
      />
    );
    return (
      <ConfirmDialog
        data-test="urgent-message-confirm-dialog"
        title={<span>{titleText}</span>}
        onConfirm={() => onConfirm({ callAllDevices: this.state.callAllDevices })}
        onRequestHide={onRequestHide}
        actionButtonText={scheduled ? 'Schedule' : 'Send'}
        cancelButtonText="Cancel"
        confirmEventKey="announcements.composer.confirm_send.send.click"
        confirmEventProperties={{
          ...this.props.eventProperties,
          organization_uuid: orgUuid,
        }}
        cancelEventKey="announcements.composer.confirm_send.cancel.click"
        cancelEventProperties={{
          ...this.props.eventProperties,
          organization_uuid: orgUuid,
        }}
      >
        <Row size="small">
          <TextWithIcon
            icon="megaphone-on"
            center={false}
            lineHeight="medium"
            size="small"
            iconSize="inherit"
          >
            <FormattedMessage id="composer.urgentMessageConfirmDialog.bulletpoints.0" />
          </TextWithIcon>
        </Row>
        <Row size="small">
          <TextWithIcon
            icon="landline-phone"
            center={false}
            lineHeight="medium"
            size="small"
            iconSize="inherit"
          >
            <FormattedMessage id="composer.urgentMessageConfirmDialog.bulletpoints.1" />
          </TextWithIcon>
        </Row>
        {hasCallAllDevicesFeature ? (
          <Checkbox
            onChange={() => {
              this.setState({ callAllDevices: !this.state.callAllDevices });
            }}
            checked={this.state.callAllDevices}
            data-test="call-all-devices-checkbox"
          >
            <Text size="small" color="graphite">
              <Row size="xxxxsmall">
                <FormattedMessage id="composer.urgentMessageConfirmDialog.callAllDevices.checkboxLabel" />
              </Row>
              <Text weight="normal">
                <FormattedMessage id="composer.urgentMessageConfirmDialog.callAllDevices.checkboxSubtext" />
              </Text>{' '}
              <Text weight="normal" italic>
                <FormattedMessage id="composer.urgentMessageConfirmDialog.callAllDevices.checkboxRecommendation" />
              </Text>
            </Text>
          </Checkbox>
        ) : null}
      </ConfirmDialog>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    totalRecipientCount: state.roleBasedMessagingSearch.totalRecipientCount,
  };
};

export { SendUrgentMessageConfirmDialog };
export default connect(mapStateToProps)(SendUrgentMessageConfirmDialog);
